import type { ItemResponse } from '@palaccount/contracts';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { exceedsAvailable } from '@/features/inventory/stock-format';

/**
 * ── THE SALES GRIDS' TWO LINE WARNINGS — stock and cost ─────────────────────
 *
 * bugs: Sales — "when a quantity is chosen, take the stock in the warehouse
 * into account and WARN before finishing if it is less than what is being
 * sold; the feature exists elsewhere (transfers)", and "the same for price
 * (below cost)". Both are the transfer screen's shape (BR-INV-07-14: warn,
 * never block): the row grows an amber hint, the grid grows an amber strip,
 * and nothing is disabled — the server has the last word at posting.
 *
 * The figures come off the item records the grid ALREADY holds (the picker's
 * `byId`, or the delivery note's `useItemsHeld` lookup): each item carries its
 * live per-warehouse stock (MD-03 panel 6) and, for a reader allowed to see
 * it, its cost. No extra request is made (NFR-42).
 *
 * One module for the four grids (quote, order, invoice, delivery), because the
 * first copy was written on the invoice and the second one would have drifted
 * from it — a different threshold on one screen reads as a bug on the other.
 */

/**
 * Where a line's stock is read from. A grid WITH a warehouse column names the
 * line's warehouse (an empty pick finds no figure and warns about nothing); a
 * grid without one — the quotation — reads the item's total across every
 * warehouse. `measure` picks ON-HAND for the delivery note: its goods are the
 * ones the confirmed order RESERVED, so "available" (on hand − reserved) would
 * count the very reservation being fulfilled and warn on every full delivery.
 */
export type StockScope =
  | { warehouseId: string; measure?: 'available' | 'onHand' }
  | { total: true };

/**
 * The figure in stock when `qty` exceeds it, else null. A blank qty, an
 * unknown item, a Service (no stock rows) or a warehouse the item has no
 * figure for all answer null — silence is the good news (BR-INV-05-03).
 */
export function shortBy(item: ItemResponse | undefined, qty: string, scope: StockScope): string | null {
  if (item === undefined || qty.trim() === '') return null;
  let have: string | undefined;
  if ('total' in scope) {
    have = item.totalAvailable;
  } else {
    const here = (item.stock ?? []).find((entry) => entry.warehouseId === scope.warehouseId);
    have = scope.measure === 'onHand' ? here?.onHand : here?.available;
  }
  // Defensive against a record that arrived without its stock block (a
  // partial fixture, an older server): no figure, no claim.
  if (typeof have !== 'string') return null;
  return exceedsAvailable(qty.trim(), have) ? have : null;
}

// ── Below cost (INV-L7) ─────────────────────────────────────────────────────

const PRICE_SCALE = 4;

/** "12.5" → 125000n at 4dp; null when the text is not a price. */
function toScaledPrice(value: string): bigint | null {
  const text = value.trim();
  if (!/^\d{1,15}(\.\d{1,4})?$/.test(text)) return null;
  const [whole = '0', fraction = ''] = text.split('.');
  return BigInt(whole) * 10_000n + BigInt(fraction.padEnd(PRICE_SCALE, '0'));
}

/**
 * The item's cost when the typed unit price is strictly below it, else null.
 * `costPrice` is ABSENT on the wire for a reader without
 * `inventory.see_item_cost` (MD-03 §9), so the hint simply never has a figure
 * to compare for them; the caller still gates on `useCan(SEE_ITEM_COST)` so a
 * stale cached record cannot leak one.
 */
export function belowCost(unitPrice: string, costPrice: string | undefined): string | null {
  if (costPrice === undefined) return null;
  const typed = toScaledPrice(unitPrice);
  const cost = toScaledPrice(costPrice);
  if (typed === null || cost === null || cost === 0n) return null;
  return typed < cost ? costPrice : null;
}

/** 4dp wire cost, printed the way the price cell prints: trailing zeros cut. */
function trimPrice(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}

// ── The three pieces of chrome ──────────────────────────────────────────────

const HINT_CLASS = 'flex items-center justify-end gap-1 px-2 text-xs text-amber-700 dark:text-amber-400';

/** Under the qty cell: "Only N in stock". */
export function LineStockHint({ available }: { available: string | null }) {
  const { t } = useTranslation();
  if (available === null) return null;
  return (
    <span className={HINT_CLASS}>
      <TriangleAlert className="size-3" aria-hidden />
      {t('sales.onlyAvailable', { qty: available })}
    </span>
  );
}

/** Under the unit-price cell: "Below cost (N)". */
export function BelowCostHint({ cost }: { cost: string | null }) {
  const { t } = useTranslation();
  if (cost === null) return null;
  return (
    <span className={HINT_CLASS}>
      <TriangleAlert className="size-3" aria-hidden />
      {t('sales.belowCost', { cost: trimPrice(cost) })}
    </span>
  );
}

/** Above the grid: the transfer screen's amber strip, drawn only when a line is short. */
export function OverStockStrip({ show }: { show: boolean }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <p
      role="status"
      className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <TriangleAlert className="size-4 shrink-0" aria-hidden />
      {t('trf.overAvailableBanner')}
    </p>
  );
}
